import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button, Card, Typography, Alert, message, Radio, Space, Divider, Collapse } from 'antd';
import { AudioOutlined, StopOutlined, ClearOutlined, DesktopOutlined, SoundOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';
import { useMultiAudioRecorder } from '../../hooks/useMultiAudioRecorder';
import { useSupabaseAuth } from '@/utils/store/supabaseAuth';

const { Title, Text } = Typography;

// 使用相对路径，通过vite代理转发到后端
const API_BASE_URL = '';

// 音频源类型
type AudioSourceType = 'microphone' | 'system' | 'mixed';

const RealTimeTranscription: React.FC = () => {
  const { isAuthenticated } = useSupabaseAuth();
  const [isRecording, setIsRecording] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [finalText, setFinalText] = useState('');
  const [partialText, setPartialText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [microphonePermission, setMicrophonePermission] = useState<'granted' | 'denied' | 'pending'>('pending');
  const [audioSource, setAudioSource] = useState<AudioSourceType>('microphone');
  const [systemAudioPermission, setSystemAudioPermission] = useState<'granted' | 'denied' | 'pending'>('pending');
  const [deviceInfo, setDeviceInfo] = useState<{audioInputs: MediaDeviceInfo[], audioOutputs: MediaDeviceInfo[]} | null>(null);
  const [showDeviceDiagnostic, setShowDeviceDiagnostic] = useState(false);
  
  const eventSourceRef = useRef<EventSource | null>(null);
  const sessionIdRef = useRef<string>('');
  const systemStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const mixedStreamRef = useRef<MediaStream | null>(null);

  // 生成会话ID
  const generateSessionId = () => {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  // 检测可用的音频设备
  const checkAudioDevices = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter(device => device.kind === 'audioinput');
      const audioOutputs = devices.filter(device => device.kind === 'audiooutput');
      
      setDeviceInfo({ audioInputs, audioOutputs });
      return audioInputs;
    } catch (error) {
      console.error('无法检测音频设备:', error);
      return [];
    }
  };

  // 设备诊断功能
  const runDeviceDiagnostic = async () => {
    setShowDeviceDiagnostic(true);
    await checkAudioDevices();
  };

  // 检查浏览器兼容性
  const checkBrowserCompatibility = () => {
    const compatibility = {
      getUserMedia: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia),
      getDisplayMedia: !!(navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia),
      audioContext: !!(window.AudioContext || (window as any).webkitAudioContext),
      enumerateDevices: !!(navigator.mediaDevices && navigator.mediaDevices.enumerateDevices)
    };
    
    const userAgent = navigator.userAgent;
    const browser = {
      chrome: /Chrome/.test(userAgent) && !/Edge/.test(userAgent),
      firefox: /Firefox/.test(userAgent),
      safari: /Safari/.test(userAgent) && !/Chrome/.test(userAgent),
      edge: /Edge/.test(userAgent),
      version: {
        chrome: (userAgent.match(/Chrome\/(\d+)/) || [])[1] || '0',
        firefox: (userAgent.match(/Firefox\/(\d+)/) || [])[1] || '0',
        safari: (userAgent.match(/Version\/(\d+)/) || [])[1] || '0',
        edge: (userAgent.match(/Edge\/(\d+)/) || [])[1] || '0'
      }
    };
    
    const environment = {
      isHTTPS: location.protocol === 'https:',
      isMobile: /Mobi|Android|iPhone|iPad/i.test(userAgent),
      isWebView: /wv|WebView/i.test(userAgent),
      isInsecureContext: !window.isSecureContext
    };
    
    return { compatibility, browser, userAgent, environment };
  };

  // 申请麦克风权限
  const requestMicrophonePermission = async () => {
    try {
      setError(null);
      setMicrophonePermission('pending'); // 重置为申请中状态
      
      // 首先检测可用的音频设备
      const audioDevices = await checkAudioDevices();
      console.log('检测到的音频设备:', audioDevices);
      
      if (audioDevices.length === 0) {
        throw new Error('未检测到任何音频输入设备');
      }
      
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true
        }
      });
      
      // 权限申请成功，立即停止流以释放资源
      stream.getTracks().forEach(track => track.stop());
      
      setMicrophonePermission('granted');
      message.success('麦克风权限已获取');
      
    } catch (error: any) {
      console.error('麦克风权限申请失败:', error);
      setMicrophonePermission('denied');
      
      let errorMsg = '麦克风权限申请失败: ' + error.message;
      
      // 根据错误类型提供更具体的指导
      if (error.name === 'NotAllowedError') {
        errorMsg += '\n\n解决方法：\n1. 点击地址栏左侧的锁头图标\n2. 选择"允许"麦克风权限\n3. 刷新页面重新尝试';
      } else if (error.name === 'NotFoundError' || error.message.includes('未检测到任何音频输入设备')) {
        errorMsg += '\n\n可能的原因和解决方法：\n';
        errorMsg += '1. **没有麦克风设备**\n   - 请连接外置麦克风或耳机\n   - 检查设备是否正确插入\n\n';
        errorMsg += '2. **麦克风被其他应用占用**\n   - 关闭正在使用麦克风的其他应用（如视频会议软件、录音软件等）\n   - 重新尝试申请权限\n\n';
        errorMsg += '3. **系统设置问题**\n   - Windows: 检查"设置 → 隐私 → 麦克风"\n   - macOS: 检查"系统偏好设置 → 安全性与隐私 → 麦克风"\n   - 确保浏览器有麦克风访问权限\n\n';
        errorMsg += '4. **浏览器设置问题**\n   - 在浏览器设置中检查麦克风权限\n   - 尝试在隐私模式下测试\n   - 清除浏览器缓存和数据';
      } else if (error.name === 'NotReadableError') {
        errorMsg += '\n\n麦克风设备无法读取，可能原因：\n1. 设备被其他应用程序占用\n2. 硬件故障\n3. 驱动程序问题\n\n请尝试重启浏览器或设备';
      }
      
      setError(errorMsg);
    }
  };

  // 申请系统音频权限（通过屏幕共享）
  const requestSystemAudioPermission = async () => {
    try {
      setError(null);
      setSystemAudioPermission('pending');
      
      // 详细的环境检查
      const { compatibility, browser, environment } = checkBrowserCompatibility();
      
      // 检查基本API支持
      if (!compatibility.getDisplayMedia) {
        throw new Error('当前浏览器不支持getDisplayMedia API');
      }
      
      // 检查安全上下文
      if (environment.isInsecureContext) {
        throw new Error('系统音频需要HTTPS环境，请使用https://访问');
      }
      
      // 移动设备检查
      if (environment.isMobile) {
        throw new Error('移动设备不支持系统音频录制，请使用桌面浏览器');
      }
      
      // WebView检查
      if (environment.isWebView) {
        throw new Error('WebView环境不支持系统音频录制');
      }
      
      // 浏览器版本检查
      const minVersions = { chrome: 72, firefox: 66, edge: 79, safari: 13 };
      const currentVersion = parseInt(
        browser.chrome ? browser.version.chrome :
        browser.firefox ? browser.version.firefox :
        browser.edge ? browser.version.edge :
        browser.safari ? browser.version.safari : '0'
      );
      
      const requiredVersion = 
        browser.chrome ? minVersions.chrome :
        browser.firefox ? minVersions.firefox :
        browser.edge ? minVersions.edge :
        browser.safari ? minVersions.safari : 999;
      
      if (currentVersion < requiredVersion) {
        throw new Error(`浏览器版本过低，需要：Chrome ${minVersions.chrome}+, Firefox ${minVersions.firefox}+, Edge ${minVersions.edge}+, Safari ${minVersions.safari}+`);
      }
      
      // 尝试申请权限
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: false,
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false
        }
      });
      
      // 检查音频轨道
      const audioTracks = stream.getAudioTracks();
      if (audioTracks.length === 0) {
        stream.getTracks().forEach(track => track.stop());
        throw new Error('未获取到音频轨道，请确保勾选"分享音频"选项');
      }
      
      // 成功，停止流并更新状态
      stream.getTracks().forEach(track => track.stop());
      setSystemAudioPermission('granted');
      message.success('系统音频权限已获取');
      
    } catch (error: any) {
      console.error('系统音频权限申请失败:', error);
      setSystemAudioPermission('denied');
      
      let errorMsg = `❌ 系统音频申请失败

🔍 错误原因：${error.message}

`;
      
      if (error.name === 'NotSupportedError' || error.message.includes('不支持')) {
        const { browser, environment } = checkBrowserCompatibility();
        errorMsg += '💡 解决方案：\n';
        errorMsg += '1. 🔄 更新浏览器到最新版本\n';
        errorMsg += '2. 💻 使用桌面版Chrome、Edge或Firefox\n';
        errorMsg += '3. 🔒 确保使用HTTPS访问\n';
        errorMsg += '4. 🎤 改用"麦克风"模式录制\n\n';
        errorMsg += `🔧 当前环境：\n`;
        errorMsg += `• 浏览器：${Object.keys(browser).find(key => browser[key as keyof typeof browser] && key !== 'version') || '未知'}\n`;
        errorMsg += `• 移动设备：${environment.isMobile ? '是' : '否'}\n`;
        errorMsg += `• HTTPS：${environment.isHTTPS ? '是' : '否'}`;
      } else if (error.name === 'NotAllowedError') {
        errorMsg += '💡 解决方案：\n';
        errorMsg += '1. 📺 重新选择屏幕/窗口共享\n';
        errorMsg += '2. ✅ 确保勾选"分享音频"选项\n';
        errorMsg += '3. 🔊 确认有音频正在播放';
      }
      
      setError(errorMsg);
    }
  };

  // 建立SSE连接
  const connectSSE = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      const sessionId = generateSessionId();
      sessionIdRef.current = sessionId;
      
      const eventSource = new EventSource(`${API_BASE_URL}/api/speech/start?sessionId=${sessionId}`);
      eventSourceRef.current = eventSource;
      
      const handleEvent = (eventType: string, handler: (data: any) => void) => {
        eventSource.addEventListener(eventType, (event) => {
          console.log(`收到${eventType}事件:`, event.data);
          try {
            const data = JSON.parse(event.data);
            handler(data);
          } catch (e) {
            console.error(`解析${eventType}事件失败:`, e);
          }
        });
      };
      
      eventSource.addEventListener('connected', () => {
        setIsConnected(true);
        resolve();
      });
      
      handleEvent('partial', (data) => {
        setPartialText(data.text);
        // 有新的转录结果，清除之前的错误提示
        if (data.text) {
          setError(null);
        }
      });
      handleEvent('final', (data) => {
        setFinalText(prev => prev + data.text + ' ');
        setPartialText('');
        // 有新的转录结果，清除之前的错误提示
        if (data.text) {
          setError(null);
        }
      });
      handleEvent('error', (data) => {
        console.error('识别错误:', data.error);
        setError('识别错误: ' + data.error);
      });
      
      eventSource.addEventListener('stopped', () => {
        console.log('收到停止信号');
        setIsConnected(false);
      });
      
      let isConnecting = true;
      eventSource.onerror = (error) => {
        console.error('SSE连接错误:', error);
        // 初始连接阶段的错误会导致连接失败
        if (isConnecting) {
          reject(new Error('SSE连接失败'));
        } else {
          // 连接后的错误显示给用户，但如果有新转录结果会自动清除
          setError('实时连接中断');
        }
      };
      
      // 超时处理
      setTimeout(() => {
        isConnecting = false;
        if (eventSource.readyState !== EventSource.OPEN) {
          reject(new Error('连接超时'));
        }
      }, 10000);
    });
  };



  // 录音数据处理回调
  const onData = (pcmData: ArrayBuffer) => {
    // 实时发送音频数据块
    if (pcmData && pcmData.byteLength > 0) {
      sendPCMData(pcmData);
    }
  };

  // 录音停止时的回调
  const onStop = (pcmData: ArrayBuffer) => {
    console.log('录音停止，最终音频数据:', pcmData.byteLength, 'bytes');
    if (pcmData && pcmData.byteLength > 0) {
      sendPCMData(pcmData);
    }
  };

  // 使用多音频源录音 hook
  const { status: recorderStatus, startRecording: startRec, stopRecording: stopRec } = useMultiAudioRecorder({
    onData,
    onStop,
    sampleRate: 16000,
    channelCount: 1,
    dataInterval: 1000,
    audioSource
  });

  // 检查音频源权限状态
  const checkAudioSourcePermission = () => {
    const { compatibility, environment } = checkBrowserCompatibility();
    
    switch (audioSource) {
      case 'microphone':
        return microphonePermission === 'granted';
      case 'system':
        return compatibility.getDisplayMedia && !environment.isMobile && !environment.isInsecureContext && systemAudioPermission === 'granted';
      case 'mixed':
        return microphonePermission === 'granted' && 
               compatibility.getDisplayMedia && !environment.isMobile && !environment.isInsecureContext &&
               systemAudioPermission === 'granted';
      default:
        return false;
    }
  };

  // 检查音频源是否可用
  const isAudioSourceAvailable = (source: AudioSourceType) => {
    const { compatibility, environment } = checkBrowserCompatibility();
    
    switch (source) {
      case 'microphone':
        return compatibility.getUserMedia && !environment.isInsecureContext;
      case 'system':
        return compatibility.getDisplayMedia && !environment.isMobile && !environment.isInsecureContext;
      case 'mixed':
        return compatibility.getUserMedia && compatibility.getDisplayMedia && !environment.isMobile && !environment.isInsecureContext;
      default:
        return false;
    }
  };

  // 请求对应的音频源权限
  const requestAudioSourcePermission = async () => {
    if (audioSource === 'microphone' && microphonePermission !== 'granted') {
      await requestMicrophonePermission();
    }
    if ((audioSource === 'system' || audioSource === 'mixed') && systemAudioPermission !== 'granted') {
      await requestSystemAudioPermission();
    }
  };

  // 发送 PCM 音频数据到后端
  const sendPCMData = async (pcmData: ArrayBuffer) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/speech/audio?sessionId=${sessionIdRef.current}`, {
        method: 'POST',
        body: new Uint8Array(pcmData),
        headers: { 'Content-Type': 'application/octet-stream' }
      });
      
      if (!response.ok) {
        console.error('发送音频数据失败:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('发送音频数据错误:', error);
    }
  };

  // 开始录音
  const startRecording = async () => {
    try {
      setError(null);
      
      // 检查并申请所需权限
      if (!checkAudioSourcePermission()) {
        await requestAudioSourcePermission();
        
        // 再次检查权限
        if (!checkAudioSourcePermission()) {
          throw new Error('未获取到必要的音频权限');
        }
      }
      
      await connectSSE();
      await startRec();
      setIsRecording(true);
    } catch (error: any) {
      console.error('开始录音失败:', error);
      setError('开始录音失败: ' + error.message);
      setIsRecording(false);
    }
  };

  // 停止录音
  const stopRecording = () => {
    try {
      setIsRecording(false);
      
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
        setIsConnected(false);
      }
      
      if (sessionIdRef.current) {
        fetch(`${API_BASE_URL}/api/speech/stop?sessionId=${sessionIdRef.current}`, {
          method: 'POST'
        }).catch(error => console.error('通知后端停止录音失败:', error));
      }
    } catch (error) {
      console.error('停止录音失败:', error);
    }
  };

  // 清空文本
  const clearText = () => {
    setFinalText('');
    setPartialText('');
  };

  // 组件挂载时申请麦克风权限（默认必须）
  useEffect(() => {
    requestMicrophonePermission();
  }, []);

  // 监听音频源变化，自动申请相应权限
  useEffect(() => {
    if (audioSource === 'system' || audioSource === 'mixed') {
      if (systemAudioPermission === 'pending') {
        // 只在用户选择了系统音频时才申请权限
        // requestSystemAudioPermission();
      }
    }
  }, [audioSource]);

  // 组件卸载时清理资源
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  // 获取状态信息
  const getStatusInfo = () => {
    if (microphonePermission === 'pending') {
      return { text: '正在申请麦克风权限...', type: 'warning' as const };
    }
    if (microphonePermission === 'denied') {
      return { text: '❌ 麦克风权限被拒绝', type: 'error' as const };
    }
    if (isRecording) {
      return { text: '🔴 正在录音...', type: 'info' as const };
    }
    if (isConnected) {
      return { text: '✅ 已连接', type: 'success' as const };
    }
    return { text: '等待开始录音...', type: 'info' as const };
  };

  // 获取显示文本
  const getDisplayText = () => {
    if (finalText || partialText) return '';
    
    if (isRecording) return '正在监听...';
    if (microphonePermission === 'pending') return '正在申请麦克风权限...';
    if (microphonePermission === 'denied') return '麦克风权限被拒绝，请在浏览器设置中允许麦克风访问';
    return '等待开始录音...';
  };

  if (!isAuthenticated) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Alert
          message="请先登录"
          description="使用实时语音转文字功能需要先登录账户"
          type="warning"
          showIcon
        />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <Card className="shadow-lg">
        <div className="text-center mb-6">
          <Title level={2} className="mb-4">
            🎤 实时语音转文字
          </Title>
          
          <Alert
            message="使用说明"
            description={
              <ul className="text-left mt-2">
                <li>• 点击"开始录音"按钮开始语音识别</li>
                <li>• 说话时会实时显示识别结果</li>
                <li>• 灰色文字为临时结果，黑色文字为最终结果</li>
                <li>• 点击"停止录音"结束识别</li>
                <li>• 支持音频格式：PCM、WAV、MP3、OPUS、SPEEX、AAC、AMR</li>
              </ul>
            }
            type="info"
            className="mb-6"
          />
        </div>
        
        {/* 音频源选择 */}
        <Card className="mb-6">
          <div className="mb-6">
            <Title level={4} className="mb-4 flex items-center text-gray-800">
              <SoundOutlined className="mr-3 text-blue-500" />
              选择音频源
            </Title>
            
            {/* 直观的卡片式选择 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* 麦克风模式 */}
              <div 
                className={`group relative cursor-pointer rounded-2xl border-2 p-6 transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl ${
                  audioSource === 'microphone' 
                    ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-blue-100 shadow-lg ring-4 ring-blue-200' 
                    : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'
                } ${isRecording ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={() => !isRecording && setAudioSource('microphone')}
              >
                {/* 选中指示器 */}
                {audioSource === 'microphone' && (
                  <div className="absolute top-4 right-4 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center animate-pulse">
                    <div className="w-3 h-3 bg-white rounded-full"></div>
                  </div>
                )}
                
                {/* 图标和标题 */}
                <div className="text-center mb-4">
                  <div className="w-16 h-16 mx-auto mb-3 bg-blue-100 rounded-full flex items-center justify-center">
                    <AudioOutlined className="text-2xl text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">🎤 麦克风模式</h3>
                  <p className="text-sm text-gray-600 mt-1">录制您的语音输入</p>
                </div>
                
                {/* 状态指示 */}
                <div className="flex items-center justify-center mb-3">
                  <div className={`flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                    microphonePermission === 'granted' ? 'bg-green-100 text-green-800' :
                    microphonePermission === 'denied' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    <div className={`w-2 h-2 rounded-full mr-2 ${
                      microphonePermission === 'granted' ? 'bg-green-500' :
                      microphonePermission === 'denied' ? 'bg-red-500' : 'bg-yellow-500'
                    }`}></div>
                    {microphonePermission === 'granted' ? '已就绪' :
                     microphonePermission === 'denied' ? '被拒绝' : '未授权'}
                  </div>
                </div>
                
                {/* 操作按钮 */}
                {microphonePermission !== 'granted' && (
                  <div className="text-center">
                    <Button 
                      size="small" 
                      type={microphonePermission === 'denied' ? 'primary' : 'default'}
                      onClick={(e) => {
                        e.stopPropagation();
                        requestMicrophonePermission();
                      }}
                      loading={microphonePermission === 'pending'}
                      className="text-xs"
                    >
                      {microphonePermission === 'denied' ? '重新授权' : '获取权限'}
                    </Button>
                  </div>
                )}
              </div>
              
              {/* 系统音频模式 */}
              <div 
                className={`group relative cursor-pointer rounded-2xl border-2 p-6 transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl ${
                  audioSource === 'system' 
                    ? 'border-green-500 bg-gradient-to-br from-green-50 to-green-100 shadow-lg ring-4 ring-green-200' 
                    : 'border-gray-200 bg-white hover:border-green-300 hover:bg-green-50'
                } ${isRecording || !isAudioSourceAvailable('system') ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={() => !isRecording && isAudioSourceAvailable('system') && setAudioSource('system')}
              >
                {/* 不支持标签 */}
                {!isAudioSourceAvailable('system') && (
                  <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full shadow-lg z-10">
                    {checkBrowserCompatibility().environment.isMobile ? '移动不支持' : 
                     checkBrowserCompatibility().environment.isInsecureContext ? '需要HTTPS' :
                     !checkBrowserCompatibility().compatibility.getDisplayMedia ? '浏览器不支持' : '不支持'}
                  </div>
                )}
                
                {/* 选中指示器 */}
                {audioSource === 'system' && (
                  <div className="absolute top-4 right-4 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center animate-pulse z-10">
                    <div className="w-3 h-3 bg-white rounded-full"></div>
                  </div>
                )}
                
                {/* 图标和标题 */}
                <div className="text-center mb-4">
                  <div className="w-16 h-16 mx-auto mb-3 bg-green-100 rounded-full flex items-center justify-center">
                    <DesktopOutlined className="text-2xl text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">🖥️ 系统音频</h3>
                  <p className="text-sm text-gray-600 mt-1">录制设备播放的声音</p>
                  {!isAudioSourceAvailable('system') && (
                    <p className="text-xs text-red-500 mt-1">当前环境不支持</p>
                  )}
                </div>
                
                {/* 状态指示 */}
                <div className="flex items-center justify-center mb-3">
                  <div className={`flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                    !isAudioSourceAvailable('system') ? 'bg-gray-100 text-gray-500' :
                    systemAudioPermission === 'granted' ? 'bg-green-100 text-green-800' :
                    systemAudioPermission === 'denied' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    <div className={`w-2 h-2 rounded-full mr-2 ${
                      !isAudioSourceAvailable('system') ? 'bg-gray-400' :
                      systemAudioPermission === 'granted' ? 'bg-green-500' :
                      systemAudioPermission === 'denied' ? 'bg-red-500' : 'bg-yellow-500'
                    }`}></div>
                    {!isAudioSourceAvailable('system') ? '不可用' :
                     systemAudioPermission === 'granted' ? '已就绪' :
                     systemAudioPermission === 'denied' ? '被拒绝' : '未授权'}
                  </div>
                </div>
                
                {/* 操作按钮 */}
                {systemAudioPermission !== 'granted' && isAudioSourceAvailable('system') && (
                  <div className="text-center">
                    <Button 
                      size="small" 
                      type={systemAudioPermission === 'denied' ? 'primary' : 'default'}
                      onClick={(e) => {
                        e.stopPropagation();
                        requestSystemAudioPermission();
                      }}
                      loading={systemAudioPermission === 'pending'}
                      className="text-xs"
                    >
                      {systemAudioPermission === 'denied' ? '重新授权' : '获取权限'}
                    </Button>
                  </div>
                )}
              </div>
              
              {/* 混合模式 */}
              <div 
                className={`group relative cursor-pointer rounded-2xl border-2 p-6 transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl ${
                  audioSource === 'mixed' 
                    ? 'border-purple-500 bg-gradient-to-br from-purple-50 to-purple-100 shadow-lg ring-4 ring-purple-200' 
                    : 'border-gray-200 bg-white hover:border-purple-300 hover:bg-purple-50'
                } ${isRecording || !isAudioSourceAvailable('mixed') ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={() => !isRecording && isAudioSourceAvailable('mixed') && setAudioSource('mixed')}
              >
                {/* 不支持/部分支持标签 */}
                {!isAudioSourceAvailable('mixed') && (
                  <div className="absolute -top-2 -right-2 bg-orange-500 text-white text-xs px-2 py-1 rounded-full shadow-lg z-10">
                    {
                      !checkBrowserCompatibility().compatibility.getUserMedia ? '麦克风不支持' :
                      !checkBrowserCompatibility().compatibility.getDisplayMedia ? '系统音频不支持' :
                      checkBrowserCompatibility().environment.isMobile ? '移动不支持' :
                      checkBrowserCompatibility().environment.isInsecureContext ? '需要HTTPS' : '不支持'
                    }
                  </div>
                )}
                
                {/* 选中指示器 */}
                {audioSource === 'mixed' && (
                  <div className="absolute top-4 right-4 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center animate-pulse z-10">
                    <div className="w-3 h-3 bg-white rounded-full"></div>
                  </div>
                )}
                
                {/* 图标和标题 */}
                <div className="text-center mb-4">
                  <div className="w-16 h-16 mx-auto mb-3 bg-purple-100 rounded-full flex items-center justify-center">
                    <div className="flex items-center text-purple-600">
                      <AudioOutlined className="text-lg" />
                      <span className="mx-1 text-sm">+</span>
                      <DesktopOutlined className="text-lg" />
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">🎵 混合模式</h3>
                  <p className="text-sm text-gray-600 mt-1">同时录制麦克风和系统音频</p>
                  {!isAudioSourceAvailable('mixed') && (
                    <p className="text-xs text-orange-500 mt-1">部分功能不可用</p>
                  )}
                </div>
                
                {/* 状态指示 */}
                <div className="flex items-center justify-center mb-3">
                  <div className={`flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                    !isAudioSourceAvailable('mixed') ? 'bg-gray-100 text-gray-500' :
                    (microphonePermission === 'granted' && systemAudioPermission === 'granted') ? 'bg-green-100 text-green-800' :
                    (microphonePermission === 'denied' || systemAudioPermission === 'denied') ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    <div className={`w-2 h-2 rounded-full mr-2 ${
                      !isAudioSourceAvailable('mixed') ? 'bg-gray-400' :
                      (microphonePermission === 'granted' && systemAudioPermission === 'granted') ? 'bg-green-500' :
                      (microphonePermission === 'denied' || systemAudioPermission === 'denied') ? 'bg-red-500' : 'bg-yellow-500'
                    }`}></div>
                    {!isAudioSourceAvailable('mixed') ? '不可用' :
                     (microphonePermission === 'granted' && systemAudioPermission === 'granted') ? '已就绪' :
                     (microphonePermission === 'denied' || systemAudioPermission === 'denied') ? '部分被拒绝' : '部分未授权'}
                  </div>
                </div>
                
                {/* 操作按钮 */}
                {isAudioSourceAvailable('mixed') && (microphonePermission !== 'granted' || systemAudioPermission !== 'granted') && (
                  <div className="flex gap-2 justify-center">
                    {microphonePermission !== 'granted' && (
                      <Button 
                        size="small" 
                        type="default"
                        onClick={(e) => {
                          e.stopPropagation();
                          requestMicrophonePermission();
                        }}
                        loading={microphonePermission === 'pending'}
                        className="text-xs"
                      >
                        🎤
                      </Button>
                    )}
                    {systemAudioPermission !== 'granted' && isAudioSourceAvailable('system') && (
                      <Button 
                        size="small" 
                        type="default"
                        onClick={(e) => {
                          e.stopPropagation();
                          requestSystemAudioPermission();
                        }}
                        loading={systemAudioPermission === 'pending'}
                        className="text-xs"
                      >
                        🖥️
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            {/* 简化的提示信息 */}
            <div className="mt-6 p-4 bg-gray-50 rounded-xl">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="w-5 h-5 text-blue-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-gray-900">使用说明</h4>
                  <div className="mt-1 text-sm text-gray-600">
                    <p>• 麦克风模式：直接录制您的语音，适合口述记录</p>
                    <p>• 系统音频：需要屏幕共享权限，请勾选"分享音频"选项</p>
                  </div>
                </div>
              </div>
            </div>
          
            
            {/* 设备诊断 - 折叠式 */}
            <Collapse 
              className="mt-4"
              size="small"
              items={[
                {
                  key: 'diagnostic',
                  label: '音频设备诊断',
                  children: (
                    <div>
                      <Button 
                        icon={<InfoCircleOutlined />}
                        onClick={runDeviceDiagnostic}
                        type="default"
                        size="small"
                        className="mb-3"
                      >
                        开始诊断
                      </Button>
                      
                      {showDeviceDiagnostic && deviceInfo && (
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="text-sm">
                            <div className="mb-2">
                              <strong>检测结果:</strong> 找到 {deviceInfo.audioInputs.length} 个音频输入设备
                            </div>
                            {deviceInfo.audioInputs.length === 0 && (
                              <Alert message="未检测到麦克风设备" type="error" />
                            )}
                            
                            <div className="mt-3">
                              <strong>浏览器支持:</strong>
                              <div className="flex gap-2 mt-1 text-xs">
                                {checkBrowserCompatibility().compatibility.getUserMedia ? (
                                  <span className="text-green-600">✓ 麦克风</span>
                                ) : (
                                  <span className="text-red-600">✗ 麦克风</span>
                                )}
                                {checkBrowserCompatibility().compatibility.getDisplayMedia ? (
                                  <span className="text-green-600">✓ 系统音频</span>
                                ) : (
                                  <span className="text-red-600">✗ 系统音频</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                }
              ]}
            />
          </div>
        </Card>
        
        {/* 录音组件 */}
        <div className="mb-6">
          <div className={`w-full h-24 md:h-28 border-2 border-gray-300 rounded-lg flex items-center justify-center mb-4 relative overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 ${
            recorderStatus === 'recording' 
              ? 'border-red-400 shadow-red-200 shadow-lg animate-pulse bg-gradient-to-br from-pink-400 to-purple-500' 
              : 'bg-gray-100 hover:shadow-gray-200'
          }`}>
            {recorderStatus === 'recording' && (
              <div className="w-full h-full bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center">
                <div className="text-white font-bold text-lg">
                  录音中...
                </div>
              </div>
            )}
            {recorderStatus === 'idle' && (
              <div className="text-gray-600 text-lg">点击开始录音</div>
            )}
            {recorderStatus === 'stopped' && (
              <div className="text-gray-600 text-lg">录音已停止</div>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              type={recorderStatus === 'recording' ? "default" : "primary"}
              icon={recorderStatus === 'recording' ? <StopOutlined /> : <AudioOutlined />}
              onClick={() => {
                if (recorderStatus === 'recording') {
                  stopRec();
                  stopRecording();
                } else {
                  startRecording();
                }
              }}
              size="large"
              disabled={!checkAudioSourcePermission()}
              className={`transition-all duration-200 ${recorderStatus === 'recording' ? 'bg-red-500 border-red-500 hover:bg-red-600' : ''}`}
            >
              {recorderStatus === 'recording' ? '停止录音' : '开始录音'}
            </Button>
            
            <Button
              size="large"
              icon={<ClearOutlined />}
              onClick={clearText}
            >
              清空文本
            </Button>
          </div>
        </div>
        
        <div className="mb-4">
          <Alert
            message={getStatusInfo().text}
            type={getStatusInfo().type}
            showIcon
          />
        </div>
        
        <Card 
          title="识别结果" 
          className="min-h-[300px]"
          bodyStyle={{ minHeight: '250px' }}
        >
          <div className="whitespace-pre-wrap break-words text-base leading-relaxed">
            {finalText && (
              <Text className="text-gray-900">{finalText}</Text>
            )}
            {partialText && (
              <Text className="text-gray-500 italic">{partialText}</Text>
            )}
            {!finalText && !partialText && (
              <Text className="text-gray-400">{getDisplayText()}</Text>
            )}
          </div>
        </Card>
        
        {error && (
          <div className="mt-4">
            <Alert
              message="错误信息"
              description={error}
              type="error"
              showIcon
              closable
              onClose={() => setError(null)}
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default RealTimeTranscription;